import React, { useState } from "react"
import { Link } from "react-router-dom"
import { Card, CardBody, Table, Modal, ModalHeader, ModalBody, Spinner } from "reactstrap"
import apiClient from "helpers/apiClient"
import { formatIndianDate, formatIndianTime } from "helpers/indianDate"

export interface PropertyInBidding {
  listingId: string
  propertyTitle: string
  bidCount: number
  leadingBidAmount: number | null
  leadingBidDate: string | null
}

interface BidRow {
  _id: string
  amount: number
  isLeading?: boolean
  createdAt: string
  userId?: { fullName?: string; email?: string }
}

const formatDate = (d: string) => formatIndianDate(d)
const formatTime = (d: string) => formatIndianTime(d)

interface PropertiesInBiddingProps {
  properties: PropertyInBidding[]
}

const PropertiesInBidding = (props: PropertiesInBiddingProps) => {
  const [modalOpen, setModalOpen] = useState(false)
  const [selectedListing, setSelectedListing] = useState<PropertyInBidding | null>(null)
  const [bids, setBids] = useState<BidRow[]>([])
  const [loadingBids, setLoadingBids] = useState(false)
  const [onlyOwnBids, setOnlyOwnBids] = useState(false)

  const openBids = (row: PropertyInBidding) => {
    setSelectedListing(row)
    setModalOpen(true)
    setBids([])
    setOnlyOwnBids(false)
    setLoadingBids(true)
    apiClient
      .get<{ success: boolean; data?: { bids?: BidRow[]; onlyOwnBids?: boolean } }>(`/api/bids/listing/${row.listingId}`)
      .then((res) => {
        const d = res.data?.data
        setBids(d?.bids ?? [])
        setOnlyOwnBids(Boolean(d?.onlyOwnBids))
      })
      .catch(() => setBids([]))
      .finally(() => setLoadingBids(false))
  }

  const closeModal = () => {
    setModalOpen(false)
    setSelectedListing(null)
    setBids([])
  }

  const properties = props.properties ?? []

  if (properties.length === 0) {
    return (
      <Card>
        <CardBody>
          <h4 className="card-title mb-4">Properties in Bidding</h4>
          <p className="text-muted mb-0">No properties currently in active bidding.</p>
        </CardBody>
      </Card>
    )
  }

  return (
    <>
      <Card>
        <CardBody>
          <h4 className="card-title mb-4">Properties in Bidding</h4>
          <p className="text-muted small mb-3">Properties with active bidding — click a row to see all bids for that property.</p>
          <div className="table-responsive">
            <Table className="align-middle table-centered table-nowrap mb-0" style={{ minWidth: "420px" }}>
              <thead className="table-light">
                <tr>
                  <th>Property</th>
                  <th className="text-end">Bids</th>
                  <th className="text-end" style={{ minWidth: "90px" }}>Leading Bid</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {properties.map((row) => (
                  <tr
                    key={row.listingId}
                    role="button"
                    onClick={() => openBids(row)}
                    className="cursor-pointer"
                    style={{ cursor: "pointer" }}
                  >
                    <td>
                      <span className="text-primary fw-medium">{row.propertyTitle}</span>
                    </td>
                    <td className="text-end">{row.bidCount}</td>
                    <td className="text-end">
                      {row.leadingBidAmount != null ? `₹${row.leadingBidAmount.toLocaleString("en-IN")}` : "—"}
                    </td>
                    <td>{row.leadingBidDate ? formatDate(row.leadingBidDate) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </CardBody>
      </Card>

      <Modal isOpen={modalOpen} toggle={closeModal} size="lg">
        <ModalHeader toggle={closeModal}>
          Bids: {selectedListing?.propertyTitle ?? "—"}
        </ModalHeader>
        <ModalBody>
          {loadingBids ? (
            <div className="text-center py-4">
              <Spinner color="primary" />
            </div>
          ) : (
            <>
              {onlyOwnBids && (
                <p className="text-muted small mb-2">Showing only your bids for this property.</p>
              )}
              <div className="table-responsive">
                <Table className="mb-0">
                  <thead className="table-light">
                    <tr>
                      {!onlyOwnBids && <th>Bidder</th>}
                      <th className="text-end">Amount</th>
                      <th>Date</th>
                      <th>Time</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bids.map((b) => (
                      <tr key={b._id}>
                        {!onlyOwnBids && <td>{b.userId?.fullName ?? "—"}</td>}
                        <td className="text-end">₹{(b.amount || 0).toLocaleString("en-IN")}</td>
                        <td>{formatDate(b.createdAt)}</td>
                        <td>{formatTime(b.createdAt)}</td>
                        <td>
                          <span className={b.isLeading ? "text-success" : "text-warning"}>
                            {b.isLeading ? "Leading" : "Outbid"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              {bids.length === 0 && !loadingBids && (
                <p className="text-muted mb-0">{onlyOwnBids ? "You have not placed any bids on this property yet." : "No bids yet."}</p>
              )}
              {selectedListing && (
                <div className="mt-3">
                  <Link to={`/property/${selectedListing.listingId}`} className="btn btn-sm btn-primary">
                    View Property
                  </Link>
                </div>
              )}
            </>
          )}
        </ModalBody>
      </Modal>
    </>
  )
}

export default PropertiesInBidding
